import { useCallback, useMemo, useState } from 'react'
import { Stack, FormControl, TableContainer, TableBody, Table, TableRow, TableCell, TextField } from '@mui/material';
import { Message } from '@aviation/cabinapp-message-service-sdk';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';

export interface ICreateDelayCommEventAttributesProps {
    item: Message;
    validationExecuted: (item : Message, valid : boolean) => void;
}

export function CreateDelayCommEventAttributes(props: ICreateDelayCommEventAttributesProps) {
    const [currentMessage, setCurrentMessage] = useState<Message>(props.item);

    const validate = useCallback(() => {
        return currentMessage.Attributes?.reduce((a, b) => {
            return a && b.AttrValue !== undefined && b.AttrValue !== null && b.AttrValue !== '';;
        }, true) ?? true;
    }, [currentMessage]);

    const messageOfEvent = useCallback(() => {
        let depDate = dayjs(currentMessage.Attributes?.find(o => o.AttrName === 'DepDate')?.AttrValue);
        let nextInfo = dayjs(currentMessage.Attributes?.find(o => o.AttrName === 'NextInfo')?.AttrValue);
        let dest = currentMessage.Attributes?.find(o => o.AttrName === 'Dest')?.AttrValue

        switch(currentMessage.Category) {
        case "ETDChange":
            return `Estimated departure changed to ${depDate.format('HH:mm')} on ${depDate.format('YYYY-MM-DD')}.`;
        case "RTDChange":
            return `Rescheduled departure changed to ${depDate.format('HH:mm')} on ${depDate.format('YYYY-MM-DD')}.`;
        case "NextInfo":
            return `Flight delayed, next update expected at ${nextInfo.format('HH:mm')} on ${nextInfo.format('YYYY-MM-DD')}.`;
        case "ReRoute":
            return `Destination changed to ${dest?.toUpperCase()}.`;
        default:
            return `Unkown event occurred.`
        }
    }, [currentMessage]);

    const friendlyName = (attrName : string | undefined) => {
        switch(attrName) {
            case 'DepDate': 
                return 'Departure date';
            case 'ArrDate':
                return 'Arrival date';
            case 'Dep':
                return 'Departure';
            case 'Dest':
                return 'Destination';
            case 'NextInfo': 
                return 'Next Info';
            default:
                return 'Unkown';
        }
    }

    const updateField = useCallback((field: string, value: any) => {
        if(field === 'EventDate' && value === 'Invalid Date')
            value = undefined;

        (currentMessage as any)[field] = value;
        const item = {...currentMessage};
        setCurrentMessage(item)

        props.validationExecuted(item, validate());
    }, [currentMessage, props, validate]);

    const hasError = useCallback((field: string, value : any) => {
        let attribute = currentMessage.Attributes?.find(o => o.AttrName === field);

        if(attribute === undefined)
            return false;

        return attribute?.AttrValue === undefined || attribute?.AttrValue === '';
    }, [currentMessage]);

    const updateAttributeValue = useCallback((field: string, value : any) => {
        let newMessage = {...currentMessage}
        let attribute = newMessage.Attributes?.find(o => o.AttrName === field);

        if(attribute === undefined)
            return;

        attribute!.AttrValue = value;

        newMessage.Message = messageOfEvent();
        setCurrentMessage(newMessage);
        props.validationExecuted(newMessage, validate());
    }, [currentMessage, props, validate, messageOfEvent]);

    const rows = useMemo(() => {
        return currentMessage?.Attributes?.map(o => {
            return {
                ...o,
                Name: friendlyName(o.AttrName),
                Value: o.AttrName === 'DepDate' || o.AttrName === 'ArrDate' || o.AttrName === 'NextInfo' ? dayjs(o.AttrValue) : o.AttrValue
            }
        }) ?? [];
    }, [currentMessage]);
    
    

    return (
        
        <Stack direction='column' spacing={2} style={{padding:20}}>
            <TableContainer>
                <Table size='small'>
                    <TableBody>
                        { (rows ?? []).map((row) => (
                            <TableRow key={row.AttrName} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell component="th" scope="row">
                                <strong>{row.Name}</strong>
                                </TableCell>
                                <TableCell align="right">
                                { (row.AttrName === 'DepDate' || row.AttrName === 'ArrDate' || row.AttrName === 'NextInfo') &&
                                (
                                    <FormControl sx={{minWidth: 200 }} size="small">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker slotProps={{textField: { size: "small" } }}
                                                // label={row.Name}
                                                value={row.Value}
                                                // onChange={(e : any) => handleMorningEnd(e)}
                                                
                                                onChange={e => updateAttributeValue(row.AttrName ??'', e)}
                                                format="HH:mm"
                                                />
                                        </LocalizationProvider>
                                    </FormControl>  
                                )}

                                { (row.AttrName === 'Dep' || row.AttrName === 'Dest') &&
                                (
                                    <FormControl sx={{minWidth: 230 }} size="small">
                                        <TextField
                                                    type="text"
                                                    variant='outlined'
                                                    color='secondary'
                                                    onChange={e => updateAttributeValue(row.AttrName ??'', e.target.value)}
                                                    value={row.Value}
                                                    error={hasError(row.AttrName, row.Value)}
                                                    // helperText={airlineNameError ? 'Please enter a name' : ''}
                                                    fullWidth
                                                    size="small"
                                        />
                                    </FormControl>
                                )}
                                </TableCell>
                            </TableRow>    
                        ))}
                        <TableRow key='reason' sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell component="th" scope="row">
                                <strong>Reason</strong>
                                </TableCell>
                                <TableCell align="right">
                                
                                    <FormControl sx={{minWidth: 230 }} size="small">
                                        <TextField
                                                    type="text"
                                                    variant='outlined'
                                                    color='secondary'
                                                    onChange={e => updateField('Reason', e.target.value)}
                                                    value={currentMessage.Reason}
                                                    
                                                    fullWidth
                                                    size="small"
                                                    multiline
                                                    minRows={3}
                                        />
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    )
}

export default CreateDelayCommEventAttributes;